import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Lock, Eye } from "lucide-react";
import { useIsStaff } from "@/hooks/useStaffRoles";
import { useSubscription } from "@/hooks/useSubscription";
import { expandPlanFeatures, getPlanEntryForPath, isPlanWriteRoute } from "@/lib/planCatalog";
import { tr } from "@/lib/tr";

/**
 * Blocks a page when the workspace plan does not include it.
 * Pages flagged `readOnlyWhenLocked` stay viewable/exportable and only their
 * create/edit routes are blocked. Staff bypass the gate.
 */
export const PlanRouteGate = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { isStaff } = useIsStaff();
  const { data, isLoading } = useSubscription();
  useTranslation(); // re-render on language change

  const entry = getPlanEntryForPath(pathname);
  if (isStaff || !entry) return <>{children}</>;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const allowed = expandPlanFeatures(data?.plan?.features).has(entry.key);
  if (allowed) return <>{children}</>;

  if (entry.readOnlyWhenLocked && !isPlanWriteRoute(pathname, entry)) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <Eye className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="flex-1 min-w-[12rem]">
            {tr(
              "هذه الشاشة للقراءة والتصدير فقط في باقتك الحالية. الإضافة والتعديل يحتاجان ترقية.",
              "This screen is read-only and exportable on your current plan. Adding or editing requires an upgrade.",
              "Cet écran est en lecture seule et exportable avec votre forfait actuel. Ajouter ou modifier nécessite une mise à niveau.",
              "آپ کے موجودہ پلان میں یہ اسکرین صرف پڑھنے اور ایکسپورٹ کے لیے ہے۔ اضافہ یا تبدیلی کے لیے اپ گریڈ ضروری ہے۔",
            )}
          </span>
          <Button asChild size="sm" variant="outline">
            <Link to="/billing/plans">
              <Sparkles className="me-2 h-4 w-4" />
              {tr("الترقية", "Upgrade", "Mettre à niveau", "اپ گریڈ")}
            </Link>
          </Button>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center p-6">
      <Card className="max-w-xl w-full border-primary/30">
        <CardContent className="pt-8 pb-6 text-center space-y-5">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold font-display">
            {tr(
              "هذه الصفحة غير مشمولة في باقتك",
              "This page is not included in your plan",
              "Cette page n'est pas incluse dans votre forfait",
              "یہ صفحہ آپ کے پلان میں شامل نہیں ہے",
            )}
          </h1>
          <p className="text-muted-foreground">
            {entry.label()}
          </p>
          <p className="text-sm text-muted-foreground">
            {tr(
              "بياناتك المسجلة محفوظة كما هي، ويمكنك الترقية للوصول إلى هذه الصفحة.",
              "Your existing data stays intact; upgrade to access this page.",
              "Vos données existantes restent intactes ; mettez à niveau pour accéder à cette page.",
              "آپ کا موجودہ ڈیٹا محفوظ ہے؛ اس صفحے تک رسائی کے لیے اپ گریڈ کریں۔",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button asChild size="lg">
              <Link to="/billing/plans">
                <Sparkles className="me-2 h-4 w-4" />
                {tr("الترقية الآن", "Upgrade now", "Mettre à niveau", "ابھی اپ گریڈ کریں")}
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/dashboard">
                {tr("لوحة المعلومات", "Dashboard", "Tableau de bord", "ڈیش بورڈ")}
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
